import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import toast from "react-hot-toast";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import WarningIcon from "~icons/material-symbols/error";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { KYOTO_REPRESENTATIVE_WARNING_COUNT } from "../../../config";
import {
  type RepresentativeCampaignReceiveMethod,
  canShowKyotoRepresentativeCampaign,
  getRepresentativeCampaignState,
  setRepresentedSubscriptionIdJwtForRepresentativeCampaign,
} from "../../../features/representative-campaign";
import { getRepresentativeCampaignApplyErrorMessage } from "../../../features/representative-campaign-error-message";
import { getCurrentTime } from "../../../libs/date";
import { t } from "../../../libs/i18n";
import {
  getCityAddressWithCache,
  requestRepresentedSubscriptionIdWithSignatureAsAgent,
} from "../../../libs/sdk";
import { getAppEnv } from "../../../libs/utils";

export const Route = createFileRoute("/_authed/kyoto-representative-campaign/")(
  {
    beforeLoad: async ({ context: { tenant } }) => {
      const cityAddress = (await getCityAddressWithCache()) ?? "";
      const currentTime = await getCurrentTime();
      if (
        !canShowKyotoRepresentativeCampaign({
          tenantSlug: tenant.slug,
          cityAddress,
          currentTime: currentTime.date,
          appEnv: getAppEnv(),
        })
      ) {
        throw redirect({ to: "/" });
      }
    },
    loader: async ({ context: { client } }) => {
      const { represent, represented } = await client.getRepresentations({});
      return getRepresentativeCampaignState({ represent, represented });
    },
    component: KyotoRepresentativeCampaignPage,
  },
);

function KyotoRepresentativeCampaignPage() {
  const {
    selfApplied,
    representedCount,
    shouldWarnRepresentedCount,
    reachedRepresentedLimit,
  } = Route.useLoaderData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] =
    useState<RepresentativeCampaignReceiveMethod | null>(() => {
      if (!selfApplied) {
        return "self";
      }
      if (!reachedRepresentedLimit) {
        return "family";
      }
      return null;
    });

  useEffect(() => {
    setSelectedMethod((current) => {
      if (current === "self" && selfApplied) {
        return reachedRepresentedLimit ? null : "family";
      }
      if (current === "family" && reachedRepresentedLimit) {
        return selfApplied ? null : "self";
      }
      if (current == null) {
        if (!selfApplied) {
          return "self";
        }
        if (!reachedRepresentedLimit) {
          return "family";
        }
      }
      return current;
    });
  }, [reachedRepresentedLimit, selfApplied]);

  const applySelf = async () => {
    setLoading(true);
    await navigate({
      to: "/kyoto-representative-campaign/processing",
      search: { method: "self" },
    });
  };

  const applyRepresentative = async () => {
    setLoading(true);
    try {
      const signedRepresentedSubscriptionIdJwt =
        await requestRepresentedSubscriptionIdWithSignatureAsAgent();
      setRepresentedSubscriptionIdJwtForRepresentativeCampaign(
        signedRepresentedSubscriptionIdJwt,
      );
      await navigate({
        to: "/kyoto-representative-campaign/processing",
        search: { method: "family" },
      });
    } catch (error) {
      toast.error(getRepresentativeCampaignApplyErrorMessage(error));
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (selectedMethod === "self" && !selfApplied) {
      await applySelf();
      return;
    }
    if (selectedMethod === "family" && !reachedRepresentedLimit) {
      await applyRepresentative();
    }
  };

  const primaryButtonText = (() => {
    if (loading) {
      return selectedMethod === "family"
        ? t("読み取り中...")
        : t("受け取り中...");
    }
    return t("同意して次へ");
  })();

  return (
    <>
      <Header
        title={t("ポイント受け取り")}
        onClickBack={() => navigate({ to: "/" })}
      />
      <div
        className={css({
          px: "20px",
          py: "24px",
          display: "flex",
          flexDir: "column",
          gap: "24px",
        })}
      >
        <section className={radioButtonCardCss}>
          <div className={titleCss}>{t("受け取り方法の選択")}</div>

          <div className={selectionCss}>
            <ReceiveMethodCard
              title={t("自分のポイントを受け取る")}
              description={t("ご自身のマイナンバーカードを使います。")}
              selected={selectedMethod === "self"}
              disabled={selfApplied}
              onSelect={() => setSelectedMethod("self")}
            />
            <ReceiveMethodCard
              title={t("家族等のポイントを受け取る")}
              description={t("ご家族等のマイナンバーカードを使います。")}
              selected={selectedMethod === "family"}
              disabled={reachedRepresentedLimit}
              onSelect={() => setSelectedMethod("family")}
            />

            {selectedMethod === "family" && <FamilyNotice />}

            {shouldWarnRepresentedCount &&
              (selectedMethod === "family" || reachedRepresentedLimit) && (
                <WarningMessage representedCount={representedCount} />
              )}
          </div>
        </section>

        <div className={actionAreaCss}>
          <button
            type="button"
            className={primaryButtonCss}
            onClick={handleNext}
            disabled={selectedMethod == null || loading}
          >
            {primaryButtonText}
            {!loading && (
              <ArrowForward
                className={css({
                  width: "24px",
                  height: "24px",
                })}
              />
            )}
          </button>
          <button
            type="button"
            className={secondaryButtonCss}
            onClick={() => navigate({ to: "/" })}
          >
            {t("もどる")}
          </button>
        </div>
      </div>
    </>
  );
}

const kyotoRepresentativeAccentColor = "#2563EB";

function ReceiveMethodCard({
  title,
  description,
  selected,
  disabled,
  onSelect,
}: {
  title: string;
  description: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={receiveMethodCardCss({ selected, disabled })}
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
    >
      <RadioCircle selected={selected} disabled={disabled} />
      <div className={methodTextCss}>
        <p className={methodTitleCss}>{title}</p>
        <p className={methodDescriptionCss}>{description}</p>
      </div>
    </button>
  );
}

function RadioCircle({
  selected,
  disabled,
}: {
  selected: boolean;
  disabled: boolean;
}) {
  return (
    <div
      className={css({
        width: "24px",
        height: "24px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      })}
    >
      <span
        className={css({
          width: "20px",
          height: "20px",
          rounded: "full",
          border: selected ? "6px solid" : "1px solid",
          borderColor: selected
            ? kyotoRepresentativeAccentColor
            : disabled
              ? "border.secondary"
              : "border.primary",
          bg: "surface.primary",
          opacity: disabled ? 0.7 : 1,
        })}
      />
    </div>
  );
}

function FamilyNotice() {
  return (
    <MessageBox>
      <p className={messageTitleCss}>{t("注意事項")}</p>
      <p className={messageBodyCss}>
        {t("・家族等のマイナンバーカードを使います")}
        <br />
        {t(
          "・家族等の登録と同時にあなたのアカウントにポイントを送ります（本人除いて最大{count}名）",
          { count: KYOTO_REPRESENTATIVE_WARNING_COUNT },
        )}
        <br />
        {t("・家族等に確認を行った上で進めてください")}
      </p>
    </MessageBox>
  );
}

function WarningMessage({ representedCount }: { representedCount: number }) {
  return (
    <MessageBox>
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
        })}
      >
        <WarningIcon
          className={css({
            color: "#EAB308",
            width: "24px",
            height: "24px",
            flexShrink: 0,
          })}
        />
        <p className={messageTitleCss}>
          {/* ICU plural を効かせるため、書式化した文字列ではなく数値を渡す */}
          {t("既に{count}名分受け取り済みです", { count: representedCount })}
        </p>
      </div>
    </MessageBox>
  );
}

function MessageBox({ children }: { children: ReactNode }) {
  return (
    <div
      className={css({
        bg: "#FEFCE8",
        border: "1px solid",
        borderColor: "border.secondary",
        rounded: "xl",
        p: "16px",
        display: "flex",
        flexDir: "column",
        gap: "4px",
      })}
    >
      {children}
    </div>
  );
}

const radioButtonCardCss = css({
  display: "flex",
  flexDir: "column",
  gap: "8px",
});

const titleCss = css({
  color: "text.primary",
  fontSize: "16px",
  lineHeight: 1.5,
});

const selectionCss = css({
  display: "flex",
  flexDir: "column",
  gap: "12px",
});

const receiveMethodCardCss = ({
  selected,
  disabled,
}: {
  selected: boolean;
  disabled: boolean;
}) =>
  css({
    bg: "surface.primary",
    border: selected ? "2px solid" : "1px solid",
    borderColor: selected ? kyotoRepresentativeAccentColor : "border.secondary",
    rounded: "xl",
    px: selected ? "19px" : "20px",
    py: selected ? "11px" : "12px",
    minH: "70px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    textAlign: "left",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.03)",
    opacity: disabled ? 0.55 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  });

const methodTextCss = css({
  minW: 0,
  display: "flex",
  flex: 1,
  flexDir: "column",
  gap: "2px",
});

const methodTitleCss = css({
  color: "text.primary",
  fontSize: "16px",
  lineHeight: 1.5,
});

const methodDescriptionCss = css({
  color: "text.secondary",
  fontSize: "14px",
  lineHeight: 1.4,
});

const messageTitleCss = css({
  color: "text.primary",
  fontSize: "16px",
  lineHeight: 1.5,
});

const messageBodyCss = css({
  color: "text.secondary",
  fontSize: "14px",
  lineHeight: 1.4,
});

const actionAreaCss = css({
  display: "flex",
  flexDir: "column",
  gap: "12px",
});

const primaryButtonCss = css({
  bg: kyotoRepresentativeAccentColor,
  color: "text.inverse",
  rounded: "xl",
  px: "24px",
  py: "12px",
  minH: "48px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  fontSize: "16px",
  fontWeight: 600,
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
  _disabled: {
    bg: "surface.disable",
    color: "text.disable",
  },
});

const secondaryButtonCss = css({
  bg: "#F8FAFC",
  color: "text.primary",
  border: "1px solid",
  borderColor: "border.secondary",
  rounded: "xl",
  px: "24px",
  py: "12px",
  minH: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  fontSize: "16px",
  fontWeight: 600,
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
});
